import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import { IUser } from '../../api/idm/interfaces';
import {
  getUsers,
  getVaultOrgs,
  getUserOrgs,
} from '../../api/idm/api';
import {
  getUserInfo,
  getUserInfoAndRoleOfVault,
  getUserAccessPermission,
} from '../../utils/user';
import { UserRoles } from '../../constants';
import { getAvailableProducts } from '../product';
import { IUserPermissions } from '../../api/vault/userPermissions';

export const KeyRoleUserInfo = 'userInfo';
export const KeyRoleUserVaultRoles = 'userVaultRoles';
export const KeyRoleUserAccessPermission = 'userAccessPermission';

export interface IUserInfo {
  userId: number;
  email: string;
  userName: string;
  displayName: string;
  firstName: string;
  lastName: string;
}

export interface IUserRole {
  orgId: number;
  orgName: string;
  roleId: number;
  roleName: string;
}

export interface IRole {
  [KeyRoleUserInfo]: IUserInfo;
  [KeyRoleUserVaultRoles]: Array<IUserRole>;
  [KeyRoleUserAccessPermission]: IUserPermissions;
  userOrgIds: Array<number>;
  userOrgsSecurityProfilesIds: Array<string>;
}

export interface IUserInfoVaultRole {
  userInfo: IUserInfo;
  userVaultRoles: Array<IUserRole>;
}

export interface IState {
  isLoaded: boolean;
  users: Array<any>;
  loggedIn: boolean;
  loginAs: IRole;
  useAs: IRole;
}

export enum ActionType {
  UpdateLoginAs = 'UserOrgRole.UpdateLoginAs',
  ClearUseAs = 'UserOrgRole.ClearUseAs',
  UpdateUseAs = 'UserOrgRole.UpdateUseAs',
  AddAllUsers = 'UserOrgRole.AddAllUsers',
  MarkUsersLoaded = 'UserOrgRole.MarkUsersLoaded',
}

export interface IUserOrgRoleAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isLoaded: false,
  users: [],
  loggedIn: false,
  loginAs: null,
  useAs: null,
};

export default function reducer(state: IState = INITIAL_STATE, action: IUserOrgRoleAction): IState {
  switch (action.type) {
    case ActionType.UpdateLoginAs:
      return {
        ...state,
        loggedIn: true,
        loginAs: action.payload.loginAs,
      };
    case ActionType.UpdateUseAs:
      return {
        ...state,
        useAs: action.payload.useAs,
      };
    case ActionType.AddAllUsers:
      return {
        ...state,
        users: action.payload.users,
      };
    case ActionType.MarkUsersLoaded:
      return {
        ...state,
        isLoaded: true,
      };
    case ActionType.ClearUseAs:
      return {
        ...state,
        useAs: null,
      };
    default:
      return state;
  }
}

export const updateLoginAs = (users: Array<IUser>, retries = 0) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const idValueMapping = state.securityProfiles?.idValueMapping ?? [];
    const isLoaded = state.securityProfiles?.isLoaded ?? false;
    if (!isLoaded) {
      if (retries < 5) {
        setTimeout(() => {
          updateLoginAs(users, retries + 1)(dispatch, getState);
        }, 500); // delay for security profiles loading
      } else {
        console.warn('[QA] Reach maximum retries waiting for security profiles to load');
      }
      return;
    }
    const user = getUserInfo(users);
    if (user) {
      const userOrgs = await getUserOrgs(user.id);
      const loginAsUser = getUserInfoAndRoleOfVault(user, userOrgs.data.data);
      const userOrgIds = loginAsUser.userVaultRoles.map((role) => role.orgId);
      const userAccessPermission = await getUserAccessPermission(loginAsUser);
      const userOrgsSecurityProfilesIds = userOrgIds
        .map((orgId) => idValueMapping.find((sp) => sp.idm_org_id === orgId))
        .filter((sp) => !!sp)
        .map((sp) => sp._id);

      const loginAs = {
        ...loginAsUser,
        userAccessPermission,
        userOrgIds,
        userOrgsSecurityProfilesIds,
      };

      dispatch({
        type: ActionType.UpdateLoginAs,
        payload: {
          loginAs,
        },
      });

      console.log('\n[QA] Login as:', loginAs);

      // note: ensure products updated accordingly for user
      getAvailableProducts()(dispatch, getState);
    } else {
      console.error('[QA] Could not fetch user information');
    }
  } catch (e) {
    console.error('[QA] Failed to construct user profile', e.toString());
  }
};

export const updateUseAs = (user: IUser, users: Array<IUser>) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    if (!user) {
      dispatch({
        type: ActionType.ClearUseAs,
      });
      // note: ensure products updated accordingly for user
      getAvailableProducts()(dispatch, getState);
      return;
    }

    const userOrgs = await getUserOrgs(user.id);

    const useAsUser = getUserInfoAndRoleOfVault(user, userOrgs.data.data);
    const userAccessPermission = await getUserAccessPermission(useAsUser);
    const useAs = { ...useAsUser, userAccessPermission };

    dispatch({
      type: ActionType.UpdateUseAs,
      payload: {
        useAs,
      },
    });

    console.log('\n\n\n[QA] Use as:', useAs);

    // note: ensure products updated accordingly for user
    getAvailableProducts()(dispatch, getState);
  } catch (e) {
    console.error('[QA] Failed to construct impersonated user profile', e.toString());
  }
};

export const loadAllUsers = (login: boolean = false) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const { data: { data: users } } = await getUsers();

    dispatch({
      type: ActionType.AddAllUsers,
      payload: {
        users,
      },
    });

    dispatch({ type: ActionType.MarkUsersLoaded });

    if (login) updateLoginAs(users)(dispatch, getState);
  } catch (e) {
    console.error('[QA] Error fetching all IDM users', e.toString());
  }
};

export function selectUserRole(state: IAppState): IRole {
  const userOrgRole = state?.user?.userOrgRole;
  if (!userOrgRole) {
    return {} as IRole;
  }

  const { loggedIn, loginAs, useAs } = userOrgRole;

  if (!loggedIn) {
    return {} as IRole;
  }

  return useAs || loginAs;
}

export function selectIsUserVaultSuperAdmin(state: IAppState): boolean {
  const role = selectUserRole(state);

  return _.some(role.userVaultRoles, ['roleName', UserRoles.VAULT_SUPER_ADMIN]);
}

export function selectIsUserVaultCustomerAdmin(state: IAppState): boolean {
  const role = selectUserRole(state);

  return _.some(role.userVaultRoles, ['roleName', UserRoles.VAULT_CUSTOMER_ADMIN]);
}

export function selectIsUserVaultEditor(state: IAppState): boolean {
  const role = selectUserRole(state);

  return _.some(role.userVaultRoles, ['roleName', UserRoles.VAULT_EDITOR]);
}

export function selectIsUserExchangeAdmin(state: IAppState): boolean {
  const role = selectUserRole(state);

  return _.some(role.userVaultRoles, ['roleName', UserRoles.EXCHANGE_ADMIN]);
}

export function selectIsUserExchangeVendor(state: IAppState): boolean {
  const role = selectUserRole(state);

  return _.some(role.userVaultRoles, ['roleName', UserRoles.EXCHANGE_VENDOR]) || _.some(role.userVaultRoles, ['roleName', UserRoles.EXCHANGE_VENDOR_PARTNER]);
}

export function selectIsAssetDeletionPermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.deleteAssets ?? false;
}

export function selectIsAssetDownloadPermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.download ?? false;
}

export function selectIsAssetEditPermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.edit ?? false;
}

export function selectIsAssetBulkEditPermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.bulkEdit ?? false;
}

export function selectIsAssetBulkTagPermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.bulkTag ?? false;
}

export function selectIsPublishPermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.publishSelected ?? false;
}

export function selectIsSharePermitted(state: IAppState): boolean {
  const role = selectUserRole(state);

  return role?.userAccessPermission?.share ?? false;
}

export const selectUserOrgIds = (state: IAppState): Array<number> => {
  const userRole = selectUserRole(state);
  return userRole ? userRole.userOrgIds : [];
};

export const selectUserId = (state: IAppState): number | null => (
  state.user?.userOrgRole?.loginAs?.[KeyRoleUserInfo]?.userId ?? null
);

export const selectUserOrgsSecurityProfiles = (state: IAppState) => {
  const userOrgs = selectUserOrgIds(state);
  const securityProfileIdValueMapping = state.securityProfiles?.idValueMapping ?? [];
  return userOrgs
    .map((orgId) => securityProfileIdValueMapping.find((sp) => sp.idm_org_id === orgId))
    .filter((sp) => !!sp);
};

export const selectUserOrgsSecurityProfilesIds = (state: IAppState): Array<string> => {
  const userRole = selectUserRole(state);
  return userRole ? userRole.userOrgsSecurityProfilesIds : [];
};

export const selectManualDownloadPriority = (state: IAppState): boolean => {
  const profiles = selectUserOrgsSecurityProfiles(state);
  if (!profiles || profiles.length === 0) {
    return false;
  }
  const [{ manual_download_priority }] = profiles;
  return !!manual_download_priority;
};

export const selectExchangeFileDownloadDisabled = (state: IAppState): boolean => {
  const profiles = selectUserOrgsSecurityProfiles(state);
  if (!profiles || profiles.length === 0) {
    return false;
  }
  const [{ exchange_file_download_disabled }] = profiles;
  return !!exchange_file_download_disabled;
};

export const selectHasManageLiveClippingPermission = (state: IAppState): boolean => {
  const role = selectUserRole(state);
  return role?.userAccessPermission?.addLiveVideo ?? false;
};

export const selectHasDownloadPermission = (state: IAppState): boolean => {
  const role = selectUserRole(state);
  return role?.userAccessPermission?.download ?? false;
};
